import { Box, Chip, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import {
  getMetrics,
  getMetricsCkd,
  getMetricsCopd,
  getMetricsHtn,
  getStatusColor,
} from "../constants";

export default function LastMeasuresCard(props) {
  const {
    aggregates,
    lastMeasures,
    isCKD,
    isCHF,
    isHTN,
    isDynamicProgram,
    alertKeys,
  } = props;

  function getDisplayNameForKey(key) {
    const displayNames = {
      systolic: "Systolic BP",
      diastolic: "Diastolic BP",
      bpm: "Heart Rate",
      weight_lbs: "Weight",
      spo2: "Pulse Oximeter",
      celsius: "Temperature",
    };
    return displayNames[key] || key;
  }

  function getUnitForKey(key) {
    const units = {
      systolic: "mmHg",
      diastolic: "mmHg",
      bpm: "bpm",
      weight_lbs: "lbs",
      spo2: "%",
      celsius: "°C",
    };
    return units[key] || "";
  }

  function getValidDate(timestamp) {
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function selectLatestMeasurementForBpm(lastMeasures) {
    const bpDate = getValidDate(lastMeasures?.bloodpressure?.timestamp);
    const poDate = getValidDate(lastMeasures?.pulseox?.timestamp);

    if (bpDate && poDate) {
      return bpDate > poDate
        ? lastMeasures.bloodpressure
        : lastMeasures.pulseox;
    }
    return lastMeasures?.bloodpressure || lastMeasures?.pulseox || null;
  }

  function getMetricsByCondition() {
    if (isDynamicProgram && lastMeasures) {
      const dynamicMetrics = [];
      const processedKeys = new Set();

      Object.entries(lastMeasures).forEach(([measureType, measureData]) => {
        if (measureData?.value && measureData?.measure_alert?.content) {
          Object.keys(measureData.measure_alert.content).forEach((key) => {
            const value = measureData.value[key];
            const status = measureData.measure_alert.content?.[key]?.status;

            if (value !== undefined && value !== null) {
              if (key === "bpm") {
                if (!processedKeys.has("bpm")) {
                  const latestBpmMeasurement =
                    selectLatestMeasurementForBpm(lastMeasures);
                  if (latestBpmMeasurement?.value?.bpm !== undefined) {
                    dynamicMetrics.push({
                      key: "bpm",
                      label: getDisplayNameForKey("bpm"),
                      value: latestBpmMeasurement.value.bpm,
                      unit: getUnitForKey("bpm"),
                      measureType:
                        latestBpmMeasurement === lastMeasures.bloodpressure
                          ? "bloodpressure"
                          : "pulseox",
                      status:
                        latestBpmMeasurement.measure_alert?.content?.bpm
                          ?.status || "normal",
                    });
                    processedKeys.add("bpm");
                  }
                }
              } else {
                dynamicMetrics.push({
                  key,
                  label: getDisplayNameForKey(key),
                  value,
                  unit: getUnitForKey(key),
                  measureType,
                  status: status || "normal",
                });
              }
            }
          });
        }
      });
      alertKeys
        .filter((key) => !dynamicMetrics.some((m) => m.key === key))
        .forEach((key) => {
          dynamicMetrics.push({
            key,
            label: getDisplayNameForKey(key),
            value: null,
            unit: getUnitForKey(key),
            status: "normal",
          });
        });

      return dynamicMetrics;
    }

    switch (true) {
      case isCHF:
        return getMetrics(lastMeasures);
      case isCKD:
        return getMetricsCkd(lastMeasures);
      case isHTN:
        return getMetricsHtn(lastMeasures);
      default:
        return getMetricsCopd(lastMeasures);
    }
  }

  return (
    <Box
      sx={{
        gap: 3,
        display: "flex",
        flexWrap: "wrap",
      }}
    >
      {aggregates && (
        <Stack
          sx={{
            p: 2,
            border: "1px solid #2196F3",
            borderRadius: 3,
            gap: "10px",
            backgroundColor: "#F5FBFF",
            width: "20%",
          }}
        >
          <Typography
            variant="p2"
            sx={{ fontWeight: 500, color: "#1F79E2", fontSize: "14px" }}
          >
            Average of Last 7 Days
          </Typography>
          <Stack spacing={1}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontSize: "14px", color: "#62748D" }}>
                Blood Pressure
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
                {aggregates?.avg_diastolic != null
                  ? `${aggregates?.avg_systolic}/${aggregates?.avg_diastolic}`
                  : "No data"}
                {aggregates?.avg_diastolic != null && (
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#62748D",
                      fontWeight: 400,
                    }}
                  >
                    {" "}
                    mmHg
                  </span>
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 4,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontSize: "14px", color: "#62748D" }}>
                Heart Rate
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
                {aggregates?.avg_bpm ?? "No data"}
                {aggregates?.avg_bpm != null && (
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#62748D",
                      fontWeight: 400,
                    }}
                  >
                    {" "}
                    bpm
                  </span>
                )}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      )}
      {getMetricsByCondition().map((measure) => (
        <Stack
          key={measure.key}
          sx={{
            p: 2,
            border: "1px solid #E6E6ED",
            borderRadius: 3,
            width: "20%",
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
              {measure.label}
            </Typography>
            {measure?.status && measure.status !== "normal" && (
              <Chip
                sx={{
                  textTransform: "capitalize",
                  fontSize: "14px",
                  fontWeight: 600,
                  padding: "2px 8px",
                  backgroundColor:
                    getStatusColor(measure?.status)?.backgroundColor ||
                    "#E7F4FF",
                  color: getStatusColor(measure?.status)?.color || "#2196F3",
                }}
                label={measure.status.replaceAll("_", " ")}
              />
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
              <Box sx={{ fontWeight: 700, fontSize: "32px" }}>
                {measure?.value || "No data"}
              </Box>
              {measure?.value && (
                <Typography sx={{ color: "#62748D", fontSize: "18px" }}>
                  {measure.unit}
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
      ))}
    </Box>
  );
}

LastMeasuresCard.propTypes = {
  isCHF: PropTypes.bool,
  isCKD: PropTypes.bool,
  isHTN: PropTypes.bool,
  aggregates: PropTypes.shape(),
  lastMeasures: PropTypes.shape(),
  alertKeys: PropTypes.arrayOf(PropTypes.string),
  isDynamicProgram: PropTypes.bool,
};

LastMeasuresCard.defaultProps = {
  isCHF: false,
  isCKD: false,
  isHTN: false,
  aggregates: {},
  lastMeasures: {},
  alertKeys: [],
  isDynamicProgram: false,
};
